import { useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase-config";
import { getShopBalance } from "../cosmetics";
import ShopItemPreview from "./ShopItemPreview";
import { TRUST_COLOR, TEXT_DARK, TEXT_MUTED, BORDER, BETRAY_COLOR, BETRAY_BG } from "../theme";

const SECTION_LABELS = {
  subtitle: "subtitles",
  border: "avatar borders",
  bubble: "chat bubble themes",
};

// "2 days, 12 hours, 43 mins" — each item now expires on its own admin-set
// clock instead of the whole catalogue wiping at a fixed month boundary.
function formatExpiryCountdown(ms) {
  const totalMinutes = Math.max(0, Math.floor(ms / 60000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const parts = [];
  if (days > 0) parts.push(`${days} day${days === 1 ? "" : "s"}`);
  if (days > 0 || hours > 0) parts.push(`${hours} hour${hours === 1 ? "" : "s"}`);
  parts.push(`${minutes} min${minutes === 1 ? "" : "s"}`);
  return parts.join(", ");
}

function Shop({ userProfile, shopItems = [], onClose }) {
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  const balance = getShopBalance(userProfile);
  const owned = userProfile.ownedItems || [];
  const activeItems = shopItems.filter((it) => it.active !== false);

  const buy = async (item) => {
    setError("");
    setBusyId(item.id);
    try {
      await httpsCallable(functions, "purchaseItem")({ itemId: item.id });
    } catch (e) {
      setError(e.message || "purchase failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ padding: 20, maxWidth: "640px", margin: "30px auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h2 style={{ fontSize: "1.125rem", fontWeight: "700" }}>shop</h2>
        <button onClick={onClose} style={{ padding: "6px 14px", background: "#fff", color: TEXT_MUTED, border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "0.8125rem" }}>
          close
        </button>
      </div>

      <div style={{ border: `1px solid ${TRUST_COLOR}`, borderRadius: "10px", padding: "16px", textAlign: "center", marginBottom: "12px" }}>
        <div style={{ fontSize: "1.75rem", fontWeight: "700", color: TRUST_COLOR }}>{balance}</div>
        <div style={{ fontSize: "0.75rem", color: TEXT_MUTED }}>points to spend this month · resets to 0 on the 1st</div>
      </div>

      <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED, textAlign: "center", marginBottom: "20px" }}>
        already own something? equip it from your profile.
      </div>

      {error && (
        <div style={{ padding: "10px", borderRadius: "8px", background: BETRAY_BG, color: BETRAY_COLOR, fontSize: "0.75rem", marginBottom: "16px", textAlign: "center" }}>
          {error}
        </div>
      )}

      {Object.keys(SECTION_LABELS).map((type) => {
        const items = activeItems.filter((it) => it.type === type);
        if (items.length === 0) return null;
        return (
          <div key={type} style={{ marginBottom: "22px" }}>
            <div style={{ fontSize: "0.75rem", fontWeight: "600", color: TEXT_MUTED, marginBottom: "8px" }}>{SECTION_LABELS[type]}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {items.map((item) => {
                const isOwned = owned.includes(item.id);
                const canAfford = balance >= item.price;
                const expiresAtMs = item.expiresAt?.toMillis ? item.expiresAt.toMillis() : null;
                return (
                  <div key={item.id} style={{ border: `1px solid ${BORDER}`, borderRadius: "10px", padding: "14px", display: "flex", alignItems: "center", gap: "14px", background: "#fff" }}>
                    <div style={{ width: "72px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <ShopItemPreview item={item} photoURL={userProfile.photoURL} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "0.875rem", fontWeight: "700", color: TEXT_DARK }}>{item.name}</div>
                      {item.description && <div style={{ fontSize: "0.75rem", color: TEXT_MUTED, marginTop: "1px" }}>{item.description}</div>}
                      <div style={{ fontSize: "0.75rem", color: TEXT_MUTED, marginTop: "1px" }}>{item.price} pts</div>
                      {expiresAtMs && (
                        <div style={{ fontSize: "0.6875rem", color: BETRAY_COLOR, marginTop: "3px", fontWeight: "600" }}>
                          available for {formatExpiryCountdown(expiresAtMs - now)}
                        </div>
                      )}
                    </div>
                    {isOwned ? (
                      <span style={{ padding: "8px 14px", fontSize: "0.75rem", fontWeight: "700", background: "#f3f5ff", color: TRUST_COLOR, borderRadius: "8px" }}>
                        owned
                      </span>
                    ) : (
                      <button
                        onClick={() => buy(item)}
                        disabled={!canAfford || busyId === item.id}
                        title={canAfford ? "" : "not enough points this month"}
                        style={{ padding: "8px 14px", fontSize: "0.75rem", fontWeight: "700", background: canAfford ? TRUST_COLOR : "#eceef3", color: canAfford ? "#fff" : TEXT_MUTED, border: "none", borderRadius: "8px" }}
                      >
                        {busyId === item.id ? "buying..." : canAfford ? "buy" : "can't afford"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {activeItems.length === 0 && (
        <p style={{ textAlign: "center", padding: "30px", color: TEXT_MUTED, fontSize: "0.8125rem" }}>the shop is empty right now — check back later.</p>
      )}
    </div>
  );
}

export default Shop;
